/**
 * SftpScheduledExportPage — SFTP scheduled export.
 * Ported from apps/dash-legacy/src/routes/_authed/reports/scheduled.tsx
 * (ScheduledReportsPage). Adapted shape: email recipients -> SFTP host/user/path.
 * Preserved: table + "New" drawer + cron picker + Run Now.
 * Added: Test Connection, auth toggle (password/key), hidden password field.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useSftpScheduledExportCreate,
	useSftpScheduledExportDelete,
	useSftpScheduledExportList,
	useSftpScheduledExportRunNow,
	useSftpScheduledExportTestConnection,
} from "./hooks";
import type {
	SftpAuthMode,
	SftpExportFormat,
	SftpScheduledExportRow,
} from "./api";

// Styling primitives -----------------------------------------------------
const CARD = "rounded-[8px] border border-zinc-200 bg-white shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder-zinc-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 disabled:cursor-not-allowed disabled:bg-zinc-50";
const SELECT = INPUT + " appearance-none";
const BTN_PRIMARY =
	"rounded-[6px] bg-gradient-to-r from-emerald-500 to-sky-500 px-3 py-1.5 text-sm font-medium text-white hover:from-emerald-600 hover:to-sky-600 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST_DANGER =
	"rounded-[6px] border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const LABEL = "text-xs font-medium text-zinc-700";
const TH =
	"px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-700";

// Constants ported from legacy -------------------------------------------
const FORMAT_LABELS: Record<SftpExportFormat, string> = {
	csv: "CSV",
	xlsx: "Excel (XLSX)",
	json: "JSON",
	pdf: "PDF",
};

const COMMON_CRONS: { label: string; value: string }[] = [
	{ label: "Every day at 08:00", value: "0 8 * * *" },
	{ label: "Every Monday at 08:00", value: "0 8 * * 1" },
	{ label: "Every hour", value: "0 * * * *" },
	{ label: "Every 6 hours", value: "0 */6 * * *" },
	{ label: "1st of month at 08:00", value: "0 8 1 * *" },
];

const TIMEZONES: string[] = [
	"UTC",
	"America/New_York",
	"America/Los_Angeles",
	"America/Chicago",
	"Europe/London",
	"Europe/Paris",
	"Asia/Tokyo",
	"Asia/Singapore",
	"Asia/Kolkata",
	"Australia/Sydney",
];

const REPORT_OPTIONS: { label: string; value: string }[] = [
	{ label: "Performance summary", value: "performance_summary" },
	{ label: "Conversions", value: "conversions" },
	{ label: "Clicks", value: "clicks" },
	{ label: "Revenue by offer", value: "revenue_by_offer" },
	{ label: "Partners rollup", value: "partners_rollup" },
];

// Helpers ----------------------------------------------------------------
function relativeTime(ts?: number | null): string {
	if (!ts) return "Never";
	const diff = Math.max(0, Date.now() / 1000 - ts);
	if (diff < 60) return "just now";
	if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
	if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
	return `${Math.floor(diff / 86400)}d ago`;
}

function statusPillClass(status: SftpScheduledExportRow["last_status"]): string {
	if (status === "ok")
		return "inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700";
	if (status === "error")
		return "inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700";
	if (status === "running" || status === "pending")
		return "inline-flex items-center rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700";
	return "inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-600";
}

// Drawer -----------------------------------------------------------------
function NewExportDrawer({
	open,
	onClose,
}: {
	open: boolean;
	onClose: () => void;
}) {
	const createMut = useSftpScheduledExportCreate();
	const testMut = useSftpScheduledExportTestConnection();

	const [name, setName] = useState("");
	const [host, setHost] = useState("");
	const [port, setPort] = useState("22");
	const [user, setUser] = useState("");
	const [authMode, setAuthMode] = useState<SftpAuthMode>("password");
	const [password, setPassword] = useState("");
	const [privateKey, setPrivateKey] = useState("");
	const [remotePath, setRemotePath] = useState("/uploads/");
	const [cron, setCron] = useState("0 8 * * *");
	const [tz, setTz] = useState("UTC");
	const [report, setReport] = useState(REPORT_OPTIONS[0]!.value);
	const [format, setFormat] = useState<SftpExportFormat>("csv");

	const canSubmit = useMemo(() => {
		if (!name.trim() || !host.trim() || !user.trim() || !remotePath.trim())
			return false;
		if (authMode === "password" && !password) return false;
		if (authMode === "key" && !privateKey.trim()) return false;
		return true;
	}, [name, host, user, remotePath, authMode, password, privateKey]);

	function buildConnectionPayload() {
		return {
			host: host.trim(),
			port: Number.parseInt(port, 10) || 22,
			user: user.trim(),
			auth_mode: authMode,
			password: authMode === "password" ? password : undefined,
			private_key: authMode === "key" ? privateKey : undefined,
			remote_path: remotePath.trim(),
		} as const;
	}

	function handleTest() {
		if (!host.trim() || !user.trim()) return;
		testMut.mutate(buildConnectionPayload());
	}

	function handleSubmit(e: FormEvent) {
		e.preventDefault();
		if (!canSubmit) return;
		createMut.mutate(
			{
				name: name.trim(),
				...buildConnectionPayload(),
				schedule_cron: cron,
				timezone_iana: tz,
				report,
				format,
				enabled: true,
			},
			{
				onSuccess: () => {
					setName("");
					setHost("");
					setUser("");
					setPassword("");
					setPrivateKey("");
					onClose();
				},
			},
		);
	}

	if (!open) return null;

	return (
		<div
			className="fixed inset-0 z-40 flex"
			role="dialog"
			aria-modal="true"
			aria-label="New SFTP Export"
			data-testid="sftp-scheduled-export-drawer"
		>
			<div
				className="absolute inset-0 bg-zinc-900/30"
				onClick={onClose}
				aria-hidden="true"
			/>
			<aside className="relative ml-auto flex h-full w-full max-w-lg flex-col bg-white shadow-2xl">
				<header className="flex items-start justify-between border-b border-zinc-200 px-5 py-4">
					<div>
						<p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
							sftp-scheduled-export
						</p>
						<h2 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-lg font-semibold text-transparent">
							New SFTP Export
						</h2>
					</div>
					<button
						type="button"
						onClick={onClose}
						className={BTN_GHOST}
						aria-label="Close drawer"
					>
						Close
					</button>
				</header>

				<form
					onSubmit={handleSubmit}
					className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4"
					data-testid="sftp-scheduled-export-create-form"
				>
					<div className="flex flex-col gap-1.5">
						<label className={LABEL} htmlFor="sftp-name">
							Name
						</label>
						<input
							id="sftp-name"
							className={INPUT}
							placeholder="Nightly partner feed"
							value={name}
							onChange={(e) => setName(e.currentTarget.value)}
							required
						/>
					</div>

					<div className="grid grid-cols-3 gap-3">
						<div className="col-span-2 flex flex-col gap-1.5">
							<label className={LABEL} htmlFor="sftp-host">
								Host
							</label>
							<input
								id="sftp-host"
								className={INPUT}
								placeholder="sftp.partner.com"
								value={host}
								onChange={(e) => setHost(e.currentTarget.value)}
								required
							/>
						</div>
						<div className="flex flex-col gap-1.5">
							<label className={LABEL} htmlFor="sftp-port">
								Port
							</label>
							<input
								id="sftp-port"
								className={INPUT}
								type="number"
								min={1}
								max={65535}
								value={port}
								onChange={(e) => setPort(e.currentTarget.value)}
							/>
						</div>
					</div>

					<div className="flex flex-col gap-1.5">
						<label className={LABEL} htmlFor="sftp-user">
							User
						</label>
						<input
							id="sftp-user"
							className={INPUT}
							placeholder="exports"
							value={user}
							onChange={(e) => setUser(e.currentTarget.value)}
							required
						/>
					</div>

					<div className="flex flex-col gap-1.5">
						<span className={LABEL}>Authentication</span>
						<div
							className="inline-flex w-fit rounded-[6px] border border-zinc-300 bg-zinc-50 p-0.5 text-xs"
							role="tablist"
							aria-label="Auth mode"
						>
							<button
								type="button"
								role="tab"
								aria-selected={authMode === "password"}
								onClick={() => setAuthMode("password")}
								className={
									"rounded-[4px] px-3 py-1 font-medium " +
									(authMode === "password"
										? "bg-white text-zinc-900 shadow-sm"
										: "text-zinc-600 hover:text-zinc-900")
								}
								data-testid="sftp-auth-password"
							>
								Password
							</button>
							<button
								type="button"
								role="tab"
								aria-selected={authMode === "key"}
								onClick={() => setAuthMode("key")}
								className={
									"rounded-[4px] px-3 py-1 font-medium " +
									(authMode === "key"
										? "bg-white text-zinc-900 shadow-sm"
										: "text-zinc-600 hover:text-zinc-900")
								}
								data-testid="sftp-auth-key"
							>
								SSH key
							</button>
						</div>
						{authMode === "password" ? (
							<input
								type="password"
								className={INPUT}
								placeholder="Password (hidden)"
								value={password}
								onChange={(e) => setPassword(e.currentTarget.value)}
								autoComplete="new-password"
								data-testid="sftp-password-input"
							/>
						) : (
							<textarea
								rows={4}
								className={
									INPUT +
									" resize-y font-mono text-xs"
								}
								placeholder="-----BEGIN OPENSSH PRIVATE KEY-----\n...\n-----END OPENSSH PRIVATE KEY-----"
								value={privateKey}
								onChange={(e) => setPrivateKey(e.currentTarget.value)}
								data-testid="sftp-private-key-input"
							/>
						)}
					</div>

					<div className="flex flex-col gap-1.5">
						<label className={LABEL} htmlFor="sftp-path">
							Remote path
						</label>
						<input
							id="sftp-path"
							className={INPUT + " font-mono text-xs"}
							placeholder="/uploads/"
							value={remotePath}
							onChange={(e) => setRemotePath(e.currentTarget.value)}
							required
						/>
					</div>

					<div className="flex flex-col gap-1.5">
						<label className={LABEL} htmlFor="sftp-cron">
							Schedule
						</label>
						<select
							id="sftp-cron-preset"
							className={SELECT}
							value={
								COMMON_CRONS.find((c) => c.value === cron)?.value ?? ""
							}
							onChange={(e) => {
								if (e.currentTarget.value) setCron(e.currentTarget.value);
							}}
						>
							<option value="">Custom</option>
							{COMMON_CRONS.map((c) => (
								<option key={c.value} value={c.value}>
									{c.label}
								</option>
							))}
						</select>
						<input
							id="sftp-cron"
							className={INPUT + " font-mono text-xs"}
							placeholder="0 8 * * *"
							value={cron}
							onChange={(e) => setCron(e.currentTarget.value)}
						/>
						<p className="text-[11px] text-zinc-500">
							5-field cron (minute hour dom month dow)
						</p>
					</div>

					<div className="grid grid-cols-2 gap-3">
						<div className="flex flex-col gap-1.5">
							<label className={LABEL} htmlFor="sftp-tz">
								Timezone
							</label>
							<select
								id="sftp-tz"
								className={SELECT}
								value={tz}
								onChange={(e) => setTz(e.currentTarget.value)}
							>
								{TIMEZONES.map((t) => (
									<option key={t} value={t}>
										{t}
									</option>
								))}
							</select>
						</div>
						<div className="flex flex-col gap-1.5">
							<label className={LABEL} htmlFor="sftp-format">
								Format
							</label>
							<select
								id="sftp-format"
								className={SELECT}
								value={format}
								onChange={(e) =>
									setFormat(e.currentTarget.value as SftpExportFormat)
								}
							>
								{(Object.keys(FORMAT_LABELS) as SftpExportFormat[]).map(
									(f) => (
										<option key={f} value={f}>
											{FORMAT_LABELS[f]}
										</option>
									),
								)}
							</select>
						</div>
					</div>

					<div className="flex flex-col gap-1.5">
						<label className={LABEL} htmlFor="sftp-report">
							Report
						</label>
						<select
							id="sftp-report"
							className={SELECT}
							value={report}
							onChange={(e) => setReport(e.currentTarget.value)}
						>
							{REPORT_OPTIONS.map((r) => (
								<option key={r.value} value={r.value}>
									{r.label}
								</option>
							))}
						</select>
					</div>

					{testMut.data ? (
						<p
							className={
								"text-xs " +
								(testMut.data.ok ? "text-emerald-700" : "text-red-600")
							}
							role="status"
							data-testid="sftp-test-result"
						>
							{testMut.data.ok
								? testMut.data.message ?? "Connection OK."
								: testMut.data.error ?? "Connection failed."}
						</p>
					) : null}
					{testMut.error ? (
						<p className="text-xs text-red-600" role="alert">
							{testMut.error.message}
						</p>
					) : null}
					{createMut.error ? (
						<p className="text-xs text-red-600" role="alert">
							{createMut.error.message}
						</p>
					) : null}

					<footer className="sticky bottom-0 mt-auto flex items-center justify-between gap-2 border-t border-zinc-200 bg-white/95 py-3 backdrop-blur">
						<button
							type="button"
							className={BTN_GHOST}
							onClick={handleTest}
							disabled={
								testMut.isPending || !host.trim() || !user.trim()
							}
							data-testid="sftp-test-connection"
						>
							{testMut.isPending ? "Testing." : "Test Connection"}
						</button>
						<div className="flex gap-2">
							<button
								type="button"
								className={BTN_GHOST}
								onClick={onClose}
								disabled={createMut.isPending}
							>
								Cancel
							</button>
							<button
								type="submit"
								className={BTN_PRIMARY}
								disabled={createMut.isPending || !canSubmit}
								data-testid="sftp-scheduled-export-submit"
							>
								{createMut.isPending ? "Saving." : "Create"}
							</button>
						</div>
					</footer>
				</form>
			</aside>
		</div>
	);
}

// Table ------------------------------------------------------------------
function ExportRow({ row }: { row: SftpScheduledExportRow }) {
	const deleteMut = useSftpScheduledExportDelete();
	const runMut = useSftpScheduledExportRunNow();

	return (
		<tr
			className="border-t border-zinc-100"
			data-testid={`sftp-scheduled-export-row-${row.id}`}
		>
			<td className={TD + " font-mono text-xs text-zinc-800"}>{row.host}</td>
			<td className={TD + " text-zinc-600"}>{row.user}</td>
			<td className={TD + " font-mono text-xs text-zinc-600"}>
				{row.remote_path}
			</td>
			<td className={TD + " font-mono text-xs text-zinc-600"}>
				{row.schedule_cron}
				<span className="ml-1 text-[10px] text-zinc-400">
					{row.timezone_iana}
				</span>
			</td>
			<td className={TD + " text-zinc-600"}>{row.report}</td>
			<td className={TD}>
				<span className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-[11px] text-zinc-600">
					{FORMAT_LABELS[row.format] ?? row.format}
				</span>
			</td>
			<td className={TD + " text-zinc-500"}>
				{relativeTime(row.last_run_at)}
			</td>
			<td className={TD}>
				{row.last_status ? (
					<span className={statusPillClass(row.last_status)}>
						{row.last_status}
					</span>
				) : (
					<span className="text-[11px] text-zinc-400">—</span>
				)}
			</td>
			<td className={TD + " text-right"}>
				<div className="flex justify-end gap-1">
					<button
						type="button"
						className={BTN_GHOST}
						onClick={() => runMut.mutate(row.id)}
						disabled={runMut.isPending}
						data-testid={`sftp-scheduled-export-run-${row.id}`}
					>
						{runMut.isPending ? "Running." : "Run Now"}
					</button>
					<button
						type="button"
						className={BTN_GHOST_DANGER}
						onClick={() => {
							if (confirm(`Delete "${row.name}"?`)) deleteMut.mutate(row.id);
						}}
						disabled={deleteMut.isPending}
						data-testid={`sftp-scheduled-export-delete-${row.id}`}
					>
						Delete
					</button>
				</div>
			</td>
		</tr>
	);
}

// Page -------------------------------------------------------------------
export function SftpScheduledExportPage() {
	const listQuery = useSftpScheduledExportList();
	const [drawerOpen, setDrawerOpen] = useState(false);

	const items = listQuery.data?.items ?? [];

	return (
		<main
			data-testid="sftp-scheduled-export-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex flex-col gap-2">
					<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
						sftp-scheduled-export
					</p>
					<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
						SFTP Scheduled Export
					</h1>
					<p className="text-sm text-zinc-500">
						Push reports to a remote SFTP endpoint on a schedule.
					</p>
				</div>
				<button
					type="button"
					className={BTN_PRIMARY}
					onClick={() => setDrawerOpen(true)}
					data-testid="sftp-scheduled-export-new"
				>
					New SFTP Export
				</button>
			</header>

			<section
				className={CARD + " overflow-hidden"}
				aria-label="SFTP Scheduled Exports"
			>
				{listQuery.isLoading ? (
					<p
						className="px-5 py-8 text-sm text-zinc-500"
						data-testid="sftp-scheduled-export-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="px-5 py-8 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<div
						className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center"
						data-testid="sftp-scheduled-export-empty"
					>
						<div
							aria-hidden="true"
							className="mb-1 h-12 w-12 rounded-full bg-gradient-to-br from-emerald-100 to-sky-100"
						/>
						<h3 className="text-base font-semibold text-zinc-900">
							No SFTP exports yet
						</h3>
						<p className="max-w-sm text-sm text-zinc-500">
							Schedule a report to be delivered to a partner SFTP endpoint.
						</p>
						<button
							type="button"
							className={BTN_PRIMARY + " mt-2"}
							onClick={() => setDrawerOpen(true)}
						>
							New SFTP Export
						</button>
					</div>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse"
							data-testid="sftp-scheduled-export-list"
						>
							<thead className="bg-zinc-50">
								<tr>
									<th className={TH}>Host</th>
									<th className={TH}>User</th>
									<th className={TH}>Path</th>
									<th className={TH}>Schedule</th>
									<th className={TH}>Report</th>
									<th className={TH}>Format</th>
									<th className={TH}>Last run</th>
									<th className={TH}>Status</th>
									<th className={TH + " text-right"}></th>
								</tr>
							</thead>
							<tbody>
								{items.map((row) => (
									<ExportRow key={row.id} row={row} />
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>

			<NewExportDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
		</main>
	);
}

export default SftpScheduledExportPage;
